import posthog from 'posthog-js';

// Initialize PostHog
export const initPostHog = () => {
  if (typeof window !== 'undefined') {
    // Check if PostHog is already initialized
    if (!posthog.__loaded) {
      // Use environment variable or default to demo mode
      const apiKey = import.meta.env.VITE_POSTHOG_API_KEY || 'phc_demo_key';
      const host = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
      
      // Skip initialization in development if no API key is set
      if (!import.meta.env.VITE_POSTHOG_API_KEY && import.meta.env.DEV) {
        console.log('PostHog: Skipped initialization (no API key in dev mode)');
        return;
      }
      
      posthog.init(apiKey, {
        api_host: host,
        // Enable session recording
        session_recording: {
          recordCrossOriginIframes: false,
        },
        // Autocapture settings
        autocapture: {
          dom_event_allowlist: ['click', 'submit'],
          url_allowlist: [],
        },
        // Privacy settings
        mask_all_text: false,
        mask_all_element_attributes: false,
        // Performance
        loaded: (posthog) => {
          if (import.meta.env.DEV) {
            console.log('PostHog loaded successfully');
          }
        },
      });
    }
  }
};

// Track custom events
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog.__loaded) {
    posthog.capture(eventName, properties);
  }
};

// Identify user
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog.__loaded) {
    posthog.identify(userId, properties);
  }
};

// Reset user on logout
export const resetUser = () => {
  if (typeof window !== 'undefined' && posthog.__loaded) {
    posthog.reset();
  }
};

// Track page views
export const trackPageView = () => {
  if (typeof window !== 'undefined' && posthog.__loaded) {
    posthog.capture('$pageview');
  }
};

// Track TTFV (Time-to-First-Value) - first successful media scan
// Phase 3 Target: < 90s for 80% of users
export const trackTTFV = (durationMs: number, metadata?: Record<string, any>) => {
  const durationSeconds = Math.round(durationMs / 1000);
  const meetsTarget = durationSeconds < 90; // Phase 3: 90s target
  
  trackEvent('ttfv_completed', {
    duration_ms: durationMs,
    duration_seconds: durationSeconds,
    meets_target: meetsTarget,
    target_threshold: 90,
    percentile_bucket: durationSeconds < 60 ? 'fast' : 
                       durationSeconds < 90 ? 'target' : 
                       durationSeconds < 120 ? 'slow' : 'critical',
    ...metadata,
  });

  // Track in Sentry for performance monitoring
  if (typeof window !== 'undefined' && (window as any).Sentry) {
    (window as any).Sentry.metrics.distribution('ttfv.duration', durationMs, {
      unit: 'millisecond',
      tags: {
        meets_target: meetsTarget.toString(),
        bucket: durationSeconds < 60 ? 'fast' : 
                durationSeconds < 90 ? 'target' : 
                durationSeconds < 120 ? 'slow' : 'critical',
      },
    });
  }
};

// Track performance baseline metrics for Phase 3 validation
export const trackPerformanceBaseline = (metrics: {
  lcp?: number;
  fid?: number;
  cls?: number;
  fcp?: number;
  ttfb?: number;
  inp?: number;
}) => {
  trackEvent('performance_baseline', {
    ...metrics,
    lcp_meets_target: metrics.lcp ? metrics.lcp < 2500 : undefined,
    fid_meets_target: metrics.fid ? metrics.fid < 100 : undefined,
    cls_meets_target: metrics.cls ? metrics.cls < 0.1 : undefined,
    timestamp: Date.now(),
  });
};

// Track cache performance for Phase 3 validation (60% hit rate target)
export const trackCachePerformance = (stats: {
  hits: number;
  misses: number;
  hitRate: number;
}) => {
  trackEvent('cache_performance', {
    ...stats,
    meets_target: stats.hitRate >= 0.6, // Phase 3: 60% hit rate target
    target_threshold: 60,
  });
};

export default posthog;
